import { createFileRoute } from "@tanstack/react-router";

import { GuidePage, type GuideFaq, type GuideSection } from "@/components/seo/GuidePage";
import { guidePageHead } from "@/lib/guide-page-seo";

const PATH = "/guides/meta-ads-learning-phase";
const HEADLINE = "How the Meta Ads learning phase works, and how to leave it";
const TITLE = "Meta Ads Learning Phase Explained | Adkiro";
const DESCRIPTION =
  "What the Meta Ads learning phase is, why ad sets get stuck in Learning Limited, what resets it, and how to structure campaigns so they exit and stay out.";

const KEY_POINTS = [
  "An ad set leaves the learning phase after roughly 50 optimisation events in a rolling seven-day window. Below that, delivery stays unstable and cost per result is not representative.",
  '"Learning Limited" means the ad set is unlikely ever to reach 50 weekly events at its current budget, audience size and optimisation event.',
  "Significant edits — budget, audience, optimisation event, creative overhaul — restart learning. Repeated edits keep an ad set permanently unstable.",
  "The usual cause of being stuck is structural: too many ad sets dividing the same conversions, so none individually reaches the threshold.",
  "The fix is almost always consolidation or choosing an optimisation event that occurs more often, not raising bids.",
];

const SECTIONS: GuideSection[] = [
  {
    heading: "What the learning phase actually is",
    body: [
      "When an ad set starts, or restarts after a significant change, Meta's delivery system has no reliable model of who converts for that particular combination of creative, audience and optimisation event. It spends the early period exploring — showing ads across a wider range of people and placements than it eventually will — to gather the signal it needs.",
      "That exploration is expensive and noisy. Cost per result during learning swings widely and is not a fair measure of whether the ad set works. This is why judging performance after two days is misleading: what is being measured is the exploration, not the outcome.",
      "The system considers an ad set to have exited learning once it has recorded approximately 50 optimisation events within a rolling seven-day window. After that point delivery stabilises, cost per result becomes more consistent, and the numbers can be trusted enough to make decisions from.",
    ],
  },
  {
    heading: "Why 50 events, and why per ad set",
    body: [
      "The threshold is a statistical one: below roughly 50 conversions a week, there is not enough data to distinguish genuine patterns from noise, so the model cannot reliably predict who is likely to convert. The exact figure is a guideline rather than a hard switch, but the principle holds — delivery optimisation needs volume.",
      "Critically, the threshold applies at the ad set level, not the campaign or account level. An account producing 200 conversions a week is not comfortably above the threshold if those conversions are spread across eight ad sets, because each one is then averaging 25 and none has stabilised.",
      "This single fact explains most cases of chronically unstable delivery. The account has enough total volume, but the structure divides it into pieces too small to learn from.",
    ],
    points: [
      "50 weekly optimisation events, per ad set, in a rolling seven-day window.",
      "The event that counts is the one the ad set optimises for, not any conversion.",
      "Account-level volume is irrelevant if it is split across many ad sets.",
      "The number is a threshold for stability, not a target to celebrate.",
    ],
  },
  {
    heading: "What Learning Limited means",
    body: [
      '"Learning Limited" is Meta\'s label for an ad set that has left the learning phase without ever reaching the event threshold, and is unlikely to reach it under current conditions. Delivery continues, but optimisation stays weak and cost per result stays volatile.',
      "It is a structural diagnosis rather than a warning about ad quality. The three inputs that determine whether an ad set can reach 50 weekly events are budget, the size of the addressable audience, and how frequently the chosen optimisation event occurs. If the combination cannot mathematically produce 50 events in a week, no amount of creative iteration will fix it.",
      "Work the arithmetic before changing anything else. If the target cost per purchase is 40 and the ad set spends 400 a week, it can produce at most ten purchases — a quarter of the threshold. That ad set will never stabilise on purchase optimisation at that budget, and the honest options are to raise the budget, consolidate it with others, or optimise for an event that happens more often.",
    ],
  },
  {
    heading: "What resets learning, and what does not",
    body: [
      "Significant edits restart the learning phase. That includes changing the optimisation event, materially changing targeting, changing the budget by a large proportion, and replacing the creative set. Small budget adjustments and adding a single new ad to an existing set generally do not.",
      "Duplicating an ad set does not preserve learning. The duplicate starts from nothing, and now competes against the original for the same audience — two unstable ad sets where there was one stable one.",
      "The practical consequence is that constant tinkering is itself a cause of poor performance. An account edited every day never accumulates enough uninterrupted delivery to stabilise, and every measurement taken from it describes the learning phase rather than the campaign.",
    ],
    points: [
      "Resets learning: optimisation event, major targeting change, large budget change, wholesale creative replacement.",
      "Usually does not: small budget changes, adding one creative to an existing set.",
      "Duplication starts learning from zero and creates auction overlap with the original.",
      "Batch intended changes together rather than making them on consecutive days.",
    ],
  },
  {
    heading: "How to get out and stay out",
    body: [
      "Consolidation is the most reliable route. Merging several small ad sets that target overlapping audiences into one concentrates conversions into a single set that can cross the threshold. Broad targeting works with this rather than against it, because it gives the delivery system a large enough pool to find converters without the advertiser pre-guessing who they are.",
      "Where volume is genuinely too low for the primary event, optimising for an earlier event in the funnel is a legitimate interim step. An account that cannot produce 50 purchases a week may comfortably produce 50 add-to-carts or 50 leads, and optimising for that gives the system enough signal to deliver sensibly. The trade-off is that the system optimises for what it is told to, so the earlier event must genuinely correlate with the outcome that matters.",
      "Then leave it alone. The most common self-inflicted cause of permanent instability is editing an ad set before it has had an uninterrupted week to stabilise.",
    ],
  },
];

const FAQS: GuideFaq[] = [
  {
    q: "How long does the Meta Ads learning phase last?",
    a: "It lasts until the ad set records roughly 50 optimisation events within a rolling seven-day window, so the duration depends on volume rather than time. A well-funded ad set with a common conversion event may exit in a few days; one with a low budget or a rare event may never exit and will be marked Learning Limited instead.",
  },
  {
    q: "What does Learning Limited mean in Meta Ads?",
    a: "It means the ad set left the learning phase without reaching about 50 optimisation events a week and is unlikely to reach it under current conditions. It is a structural problem caused by some combination of budget, audience size and how often the chosen optimisation event occurs — not a judgement about creative quality.",
  },
  {
    q: "Does editing an ad set restart the learning phase?",
    a: "Significant edits do. Changing the optimisation event, materially changing targeting, changing the budget by a large proportion, or replacing the creative set will restart learning. Small budget adjustments and adding a single ad to an existing set generally will not. Duplicating an ad set does not carry learning over — the duplicate begins from zero.",
  },
  {
    q: "Should I use fewer ad sets to exit the learning phase?",
    a: "Usually yes. The 50-event threshold applies per ad set, so splitting a fixed number of conversions across many ad sets can leave every one of them below it. Consolidating overlapping ad sets concentrates conversions into fewer sets that can each cross the threshold and stabilise.",
  },
  {
    q: "Can I just increase the bid to get out of learning?",
    a: "No. The learning phase depends on the number of optimisation events recorded, not on bid level. Raising bids increases cost per result without necessarily producing the volume needed to stabilise. The effective levers are budget, audience size, the choice of optimisation event, and how many ad sets the conversions are divided between.",
  },
];

export const Route = createFileRoute("/guides/meta-ads-learning-phase")({
  head: () =>
    guidePageHead({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      headline: HEADLINE,
      published: "2026-09-07",
      modified: "2026-09-07",
      faqs: FAQS,
    }),
  component: Page,
});

function Page() {
  return (
    <GuidePage
      eyebrow="Delivery"
      headline={HEADLINE}
      standfirst="Most ad sets that never stabilise are not badly targeted — they are structurally unable to reach the volume the delivery system needs. The threshold is per ad set, which is where the problem usually starts."
      keyPoints={KEY_POINTS}
      sections={SECTIONS}
      faqs={FAQS}
      updated="7 September 2026"
      readingMinutes={7}
      related={[
        {
          href: "/guides/meta-ads-campaign-structure",
          label: "How to structure Meta Ads campaigns",
        },
        {
          href: "/guides/why-meta-ad-costs-rise",
          label: "Why Meta ad costs rise while conversions fall",
        },
        { href: "/meta-ads-audit", label: "What a Meta Ads account review covers" },
      ]}
    />
  );
}
